import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { PlayArrow as PlayIcon, Storage as RollupIcon, Update as UpdateIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { rollup } from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { MetricStat } from '@/components/MetricStat';
import { TenantSelect } from '@/components/TenantSelect';
import { PageShell } from '@/layout/PageShell';
import { utcStamp } from '@/utils/format';
import type { AbdmMetricsDaily, RollupResult } from '@/types/backend';

/**
 * Three things in one place:
 *
 *   - Trigger: roll yesterday now, or roll a specific date
 *   - Backfill: a date range, drives one day at a time server-side
 *   - Inspect: read raw rollup rows for a tenant + date range
 *
 * The scheduled job runs at 00:15 UTC daily. The "Run yesterday" button
 * exists for the days where the cron failed or the operator just wants
 * to be sure.
 */

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}
function yesterdayUTC(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function MetricsRollupPage() {
  const theme = useTheme();
  const [singleDate, setSingleDate] = useState(yesterdayUTC());
  const [from, setFrom] = useState(yesterdayUTC());
  const [to, setTo] = useState(yesterdayUTC());
  const [tenantId, setTenantId] = useState('');
  const [readFrom, setReadFrom] = useState(yesterdayUTC());
  const [readTo, setReadTo] = useState(todayUTC());

  const [lastRun, setLastRun] = useState<RollupResult | null>(null);
  const [lastBackfill, setLastBackfill] = useState<Record<string, RollupResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<'run' | 'backfill' | null>(null);

  const handleRun = async () => {
    setError(null);
    setRunning('run');
    try {
      const result = await rollup.run(singleDate);
      setLastRun(result);
    } catch (e) {
      setError((e as Error).message ?? 'Run failed.');
    } finally {
      setRunning(null);
    }
  };

  const handleBackfill = async () => {
    setError(null);
    if (from > to) {
      setError('From must be before or equal to To.');
      return;
    }
    setRunning('backfill');
    try {
      const result = await rollup.backfill(from, to);
      setLastBackfill(result);
    } catch (e) {
      setError((e as Error).message ?? 'Backfill failed.');
    } finally {
      setRunning(null);
    }
  };

  const readKey = useMemo(
    () => (tenantId && readFrom && readTo ? ['rollup-read', tenantId, readFrom, readTo] : null),
    [tenantId, readFrom, readTo],
  );
  const { data: rows, error: readError, isLoading: readLoading } = useSWR(
    readKey,
    () => rollup.read(tenantId, readFrom, readTo),
  );

  const aggregate = useMemo(() => {
    if (!rows) return null;
    const total = rows.reduce((acc, r) => acc + r.count, 0);
    const fail = rows.filter((r) => r.status === 'FAIL').reduce((acc, r) => acc + r.count, 0);
    return { total, fail };
  }, [rows]);

  return (
    <PageShell
      kicker="PLATFORM · ROLLUP"
      title="Metrics Rollup"
      subtitle="Daily aggregation of event-log counts. The scheduled job runs at 00:15 UTC and rolls up the previous day; admin triggers below cover gaps and backfills."
      width="wide"
    >
      <Stack spacing={3}>
        {/* Run a single day */}
        <Section
          title="Run a single day"
          description="Recompute the rollup for one UTC date. Idempotent — repeated runs overwrite in place via the unique (tenantId, date, milestone, status) index."
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TextField
              type="date"
              label="UTC date"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={handleRun}
              disabled={running !== null}
              sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
            >
              {running === 'run' ? 'Running…' : 'Run rollup'}
            </Button>
          </Stack>
          {lastRun && (
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <MetricStat label="Date" value={lastRun.date} />
              <MetricStat label="Tenants" value={lastRun.tenantCount} tone="info" />
              <MetricStat label="Rows written" value={lastRun.rowCount} tone="success" />
            </Box>
          )}
        </Section>

        {/* Backfill */}
        <Section
          title="Backfill a range"
          description="Drives the daily rollup once per UTC day in the inclusive range. Use after a deploy gap or to seed the rollup for new tenants."
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TextField type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
            <TextField type="date" label="To"   value={to}   onChange={(e) => setTo(e.target.value)}   size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
            <Button
              variant="contained"
              startIcon={<UpdateIcon />}
              onClick={handleBackfill}
              disabled={running !== null}
              sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
            >
              {running === 'backfill' ? 'Backfilling…' : 'Backfill'}
            </Button>
          </Stack>
          {lastBackfill && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Backfilled {Object.keys(lastBackfill).length} day{Object.keys(lastBackfill).length === 1 ? '' : 's'}.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DataTable<{ date: string; result: RollupResult }>
                  rows={Object.entries(lastBackfill).map(([date, result]) => ({ date, result }))}
                  rowKey={(r) => r.date}
                  empty={{ icon: RollupIcon, title: 'No days returned.' }}
                  columns={[
                    { header: 'Date', cell: (r) => r.date, width: 140 },
                    { header: 'Tenants', cell: (r) => r.result.tenantCount, align: 'right', width: 100 },
                    { header: 'Rows', cell: (r) => r.result.rowCount, align: 'right', width: 100 },
                  ]}
                />
              </Box>
            </Box>
          )}
        </Section>

        {/* Inspect */}
        <Section
          title="Inspect rollup rows"
          description="Read the raw daily rows for a tenant. Use this to diff a dashboard count against the underlying rollup."
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TenantSelect value={tenantId} onChange={setTenantId} sx={{ minWidth: 280 }} />
            <TextField type="date" label="From" value={readFrom} onChange={(e) => setReadFrom(e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
            <TextField type="date" label="To"   value={readTo}   onChange={(e) => setReadTo(e.target.value)}   size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
          </Stack>
          {aggregate && (
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <MetricStat label="Total events" value={aggregate.total.toLocaleString()} />
              <MetricStat label="FAIL events" value={aggregate.fail.toLocaleString()} tone={aggregate.fail > 0 ? 'danger' : 'success'} />
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <DataTable<AbdmMetricsDaily>
              rows={rows}
              rowKey={(r) => r.id}
              isLoading={readLoading}
              error={readError ? { message: (readError as Error).message } : null}
              empty={{
                icon: RollupIcon,
                title: tenantId ? 'No rows in this range.' : 'Pick a tenant to inspect.',
                description: tenantId ? 'The scheduled job hasn\'t closed these days yet, or there were no events.' : undefined,
              }}
              columns={[
                { header: 'Date', cell: (r) => r.date, width: 130 },
                { header: 'Milestone', cell: (r) => r.milestone, width: 110 },
                { header: 'Status', cell: (r) => r.status, width: 100 },
                { header: 'Count', cell: (r) => r.count.toLocaleString(), align: 'right', width: 100 },
                { header: 'Rolled up', cell: (r) => <Typography variant="caption" color="text.secondary">{utcStamp(r.rolledUpAt)}</Typography> },
              ]}
            />
          </Box>
        </Section>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      </Stack>
    </PageShell>
  );
}

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: theme.palette.surface.card,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
        p: 3,
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 600 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>{description}</Typography>
      </Stack>
      {children}
    </Box>
  );
}
